// src/components/NewsPage.tsx

import React, { useState, useEffect } from 'react';
import api from '../services/api'; 
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card.tsx';
import { Badge } from './ui/badge.tsx';
import { 
    Megaphone, 
    Briefcase, 
    Newspaper, 
    HeartCrack, 
    CalendarDays,
    UserCircle 
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

interface AnnouncementAuthor {
    id: number;
    FirstName: string;
    LastName: string;
    profile?: { ProfilePicture?: string };
}

interface Announcement {
    id: number;
    Title: string;
    Content: string;
    Description: string;
    Category: string;
    PublishDate: string;
    Image?: string | null;
    author?: AnnouncementAuthor;
}

export function NewsPage() {
    const navigate = useNavigate();
    const [announcements, setAnnouncements] = useState<Announcement[]>([]);
    const [loading, setLoading] = useState(true);

    const getFullImageUrl = (urlPath: string | null | undefined) => {
        if (!urlPath) return undefined;
        if (urlPath.startsWith('http')) return urlPath;
        const baseUrl = api.defaults.baseURL?.replace('/api', '') || 'https://sdal-project-backend.onrender.com';
        return `${baseUrl}${urlPath}`;
    };

    const getCategoryStyle = (category: string) => {
        switch (category) {
            case "IS_ILANI": return { icon: <Briefcase className="h-5 w-5" />, label: "İş İlanı", color: "bg-blue-50 text-blue-700" };
            case "VEFAT": return { icon: <HeartCrack className="h-5 w-5" />, label: "Vefat", color: "bg-gray-100 text-gray-700" };
            case "HABER": return { icon: <Newspaper className="h-5 w-5" />, label: "Haber", color: "bg-green-50 text-green-700" };
            default: return { icon: <Megaphone className="h-5 w-5" />, label: "Duyuru", color: "bg-red-50 text-red-700" };
        }
    };

    useEffect(() => {
        const fetchAnnouncements = async () => {
            try {
                const response = await api.get('/announcements/');
                setAnnouncements(response.data);
            } catch (error) {
                console.error("Duyurular yüklenemedi:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchAnnouncements();
    }, []);

    if (loading) return <div className="text-center py-20 text-slate-500 animate-pulse">Duyurular Yükleniyor...</div>;

    return (
        <div className="container mx-auto px-4 py-8">
            {/* 3 SÜTUNLU GRID */}
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                {announcements.map((ann, index) => {
                    const style = getCategoryStyle(ann.Category);
                    return (
                        <motion.div
                            key={ann.id}
                            initial={{ opacity: 0, y: 20 }} 
                            animate={{ opacity: 1, y: 0 }} 
                            transition={{ duration: 0.4, delay: index * 0.1 }}
                        >
                            {/* KARTIN TAMAMI TIKLANABİLİR YAPILDI VE DETAY SAYFASINA BAĞLANDI */}
                            <Card 
                                onClick={() => navigate(`/duyurular/${ann.id}`)}
                                className="h-full flex flex-col hover:shadow-xl hover:-translate-y-1 transition-all duration-300 border-none bg-white rounded-2xl overflow-hidden group cursor-pointer"
                            >
                                {/* RESİM ALANI (Üstte) */}
                                <div className="relative h-48 w-full overflow-hidden bg-slate-100">
                                    {ann.Image ? (
                                        <img 
                                            src={getFullImageUrl(ann.Image)} 
                                            alt={ann.Title}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-slate-300">
                                            {React.cloneElement(style.icon as React.ReactElement, { className: "h-16 w-16" })}
                                        </div>
                                    )}
                                    <Badge className={`absolute top-4 left-4 border-none shadow-sm ${style.color}`}>
                                        {style.label}
                                    </Badge>
                                </div>

                                <CardHeader className="pb-2">
                                    <div className="flex items-center text-[11px] text-slate-400 gap-1 mb-2 font-medium">
                                        <CalendarDays className="h-3 w-3" />
                                        {new Date(ann.PublishDate).toLocaleDateString('tr-TR')}
                                    </div>
                                    <CardTitle className="text-xl font-bold text-slate-800 line-clamp-2 leading-tight group-hover:text-[#A8322D] transition-colors">
                                        {ann.Title}
                                    </CardTitle>
                                </CardHeader>

                                <CardContent className="flex-1 flex flex-col justify-between">
                                    <CardDescription className="line-clamp-3 text-sm mb-6 text-slate-600">
                                        {ann.Description || "Detaylar için tıklayınız..."}
                                    </CardDescription>

                                    {/* YAZAR BİLGİSİ (Alt Kısım) */}
                                    <div className="pt-4 border-t border-slate-50 flex items-center justify-between">
                                        {ann.author ? (
                                            <button 
                                                onClick={(e) => {
                                                    e.stopPropagation(); // Karta tıklama olayının (detaya gitmenin) tetiklenmesini engeller
                                                    navigate(`/profil?id=${ann.author?.id}`);
                                                }}
                                                className="flex items-center gap-2 text-slate-500 hover:text-blue-600 transition-colors group/author"
                                            >
                                                <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center overflow-hidden border border-slate-200">
                                                    {ann.author.profile?.ProfilePicture ? (
                                                        <img src={getFullImageUrl(ann.author.profile.ProfilePicture)} className="w-full h-full object-cover" alt="" />
                                                    ) : <UserCircle className="w-5 h-5" />}
                                                </div>
                                                <span className="text-xs font-bold truncate max-w-[150px]">
                                                    {ann.author.FirstName} {ann.author.LastName}
                                                </span>
                                            </button>
                                        ) : (
                                            <div className="text-xs text-slate-400 italic">Sistem Duyurusu</div>
                                        )}
                                        
                                        {/* Eski 'Oku' butonu yerine estetik bir sağa ok ikonu eklendi, group hover ile kayar */}
                                        <div className="text-[#A8322D] text-xs font-bold flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all transform translate-x-2 group-hover:translate-x-0">
                                            Detay &rarr;
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </motion.div>
                    );
                })}
            </div>

            {announcements.length === 0 && (
                <motion.div 
                    initial={{ opacity: 0, y: 20 }} 
                    animate={{ opacity: 1, y: 0 }} 
                    transition={{ duration: 0.5 }}
                    className="text-center py-12 bg-slate-50 rounded-lg border border-dashed border-slate-300"
                >
                    <Megaphone className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">Henüz yayınlanmış bir duyuru bulunmamaktadır.</p>
                </motion.div>
            )}
        </div>
    );
}